import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, CircularProgress, Snackbar, Alert, MenuItem, Typography } from "@mui/material";
import { makeEmployeeTransaction } from "../../../services/Employee/EmployeeApi";
import { getBanks } from "../../../services/dashboard/banks";
 
const SalaryPayment = ({ open, employeeId, onClose, onPaid, banks = [] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today,
    orginal_amount: "",
    total: "",
    action_type: "payment",
    employee_id: employeeId ?? "",
    bank_id: "",
    note: "",
    file: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // new state to hold fetched banks
  const [banksList, setBanksList] = useState(banks || []);

  useEffect(() => {
    setForm((f) => ({ ...f, employee_id: employeeId ?? "" }));
  }, [employeeId]);

  // load banks when dialog opens (or fallback to props.banks)
 useEffect(() => {
  let mounted = true;
  const load = async () => {
    if (banksList.length > 0) return; // ✅ منع إعادة التحميل
    try {
      const res = await getBanks();
      const list = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : []);
      if (!mounted) return;
      setBanksList(list.length ? list : (banks || []));
    } catch (err) {
      if (!mounted) return;
      setBanksList(banks || []);
    }
  };
  if (open) load();
  return () => { mounted = false; };
}, [open, banks]);


  // compute total automatically when amounts change
  useEffect(() => {
    const o = parseFloat(form.orginal_amount) || 0;
    const calc = o  ;
    // set total only if orginal provided
    setForm((f) => ({ ...f, total: f.orginal_amount ? String(Number.isFinite(calc) ? calc.toFixed(2) : calc) : "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.orginal_amount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  
  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setForm((f) => ({ ...f, file }));
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // build payload object — makeEmployeeTransaction will convert to FormData
      const payload = {
        date: form.date,
        orginal_amount: form.orginal_amount,
        // force bouns and discount to "0"
        bouns: "0",
        discount: "0",
        total: form.total,
        action_type: form.action_type,
        employee_id: form.employee_id,
        bank_id: form.bank_id,
        note: form.note,
        file: form.file,
      };
      const res = await makeEmployeeTransaction(payload);
      setSnackbar({ open: true, message: "تم سداد المرتب بنجاح", severity: "success" });
      if (typeof onPaid === "function") onPaid(res);

      // dispatch global event so EmployeeAccountStatement (or any listener) can refresh
      try {
        window.dispatchEvent(new CustomEvent("employeeAccountStatementUpdated", {
          detail: { employeeId: form.employee_id, transaction: res }
        }));
      } catch (e) {
        console.warn("event dispatch failed", e);
      }

      if (typeof onClose === "function") onClose();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err?.message || "فشل في سداد المرتب", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>سداد المرتب</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">معرف الموظف: {employeeId ?? "-"}</Typography>
          </Box>

          {/* تاريخ العملية */}
          <TextField
            label="التاريخ"
            name="date"
            value={form.date}
            onChange={handleChange}
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          {/* نوع العملية - ثابت إلى سداد */}
          <TextField
            label="نوع العملية"
            name="action_type"
            value={"سداد"}
            fullWidth
            margin="normal"
            disabled
            helperText="نوع العملية ثابت: سداد"
          />

          {/* المبلغ الأصلي */}
          <TextField
            label="المرتب"
            name="orginal_amount"
            value={form.orginal_amount}
            onChange={handleChange}
            fullWidth
            margin="normal"
            type="number"
          />

          {/* الإجمالي - للعرض فقط */}
          <TextField
            label="المدفوع"
            name="total"
            value={form.total}
            InputProps={{ readOnly: true }}
            fullWidth
            margin="normal"
          />

          {/* اختيار الخزنة / البنك - عرض قائمة أسماء الخزن */}
          <TextField
            select
            label="الخزنة / البنك"
            name="bank_id"
            value={form.bank_id}
            onChange={handleChange}
            fullWidth
            margin="normal"
            helperText="اختر الخزنة أو البنك"
          >
            <MenuItem value="">-- بدون --</MenuItem>
            {banksList && banksList.length > 0 ? (
              banksList.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name ?? b.title ?? b.id}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>لا توجد خزن</MenuItem>
            )}
          </TextField>

          {/* رفع ملف */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <input
              accept="image/*,application/pdf"
              id="salary-file-upload"
              type="file"
              onChange={handleFile}
              style={{ display: "block" }}
            />
          </Box>

          {/* ملاحظة */}
          <TextField
            label="ملاحظة (اختياري)"
            name="note"
            value={form.note}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={handleSubmit} disabled={submitting || !form.employee_id || !form.orginal_amount} variant="contained">
            {submitting ? <CircularProgress size={20} /> : "سداد"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default SalaryPayment;
